var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useCallback, useEffect, useMemo, useRef, useState, } from "react";
import { Row, Col, Button, MessageBanner, TextLink, } from "@folio/stripes/components";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useResolveCollection } from "../../hooks/title/useResolveCollection";
import { translateCollectionType } from "../../utils/collectionTypeUtils";
import { formatCollectionOptionLabel } from "../../utils/collectionDisplayUtils";
import { useItemEditor } from "../../hooks/title/useItemEditor";
import { usePermissions } from "../../hooks/usePermissions";
import { RoleGroups } from "../../types/RoleEnum";
import { useContentRows } from "../../utils/toContentRows";
import { FormSectionModal } from "./FormSectionModal";
import { useFieldArrayErrors } from "../../hooks/useFieldArrayErrors";
import { EntityRowList } from "./EntityRowList";
import { CreateCollectionInlineModal } from "./CreateCollectionInlineModal";
import { TitleIssnMediumWarning } from "./TitleIssnMediumWarning";
import css from "./FormModal.module.css";
var TitleCollectionsForm = function (_a) {
    var titleId = _a.titleId, collectionOptions = _a.collectionOptions, addCollectionOption = _a.addCollectionOption, resetCollectionOptions = _a.resetCollectionOptions, onCollectionFilter = _a.onCollectionFilter;
    var formatMessage = useIntl().formatMessage;
    var resolveCollectionById = useResolveCollection().resolveCollectionById;
    var hasAnyRole = usePermissions().hasAnyRole;
    var canCreateCollection = hasAnyRole(RoleGroups.COLLECTION_EDITOR_ROLES);
    var _b = useFormContext(), control = _b.control, getValues = _b.getValues;
    var errorMessages = useFieldArrayErrors("titleCollections");
    // DB-787: the warning follows the Series / editions as they are edited, before saving
    var watchedCollections = useWatch({ control: control, name: "titleCollections" });
    var _c = useFieldArray({ control: control, name: "titleCollections" }), collectionFields = _c.fields, appendCollection = _c.append, updateCollection = _c.update, removeCollection = _c.remove;
    var _d = useItemEditor({
        appendItem: appendCollection,
        updateItem: updateCollection,
        createEmpty: function () { return ({
            collection: undefined,
            partNumber: "",
        }); },
    }), isOpen = _d.isOpen, isEditing = _d.isEditing, draft = _d.draft, setDraft = _d.setDraft, openEditor = _d.openEditor, closeEditor = _d.closeEditor, saveItem = _d.saveItem;
    var collectionRequestIdRef = useRef(0);
    var _e = useState(false), createCollectionOpen = _e[0], setCreateCollectionOpen = _e[1];
    useEffect(function () {
        if (isOpen) {
            setCreateCollectionOpen(false);
            resetCollectionOptions();
        }
    }, [isOpen, resetCollectionOptions]);
    useEffect(function () {
        if (!isOpen || !draft.collection)
            return;
        addCollectionOption(draft.collection);
    }, [isOpen, draft.collection, addCollectionOption]);
    var handleCreatedCollection = useCallback(function (created) {
        if (!created.id)
            return;
        setDraft(function (prev) { return (__assign(__assign({}, prev), { collection: created })); });
        addCollectionOption(created);
        setCreateCollectionOpen(false);
    }, [addCollectionOption, setDraft]);
    var handleCollectionSelect = useCallback(function (collectionId) { return __awaiter(void 0, void 0, void 0, function () {
        var requestId, collection;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    requestId = ++collectionRequestIdRef.current;
                    if (!(collectionId === null || collectionId === void 0 ? void 0 : collectionId.trim())) {
                        setDraft(function (prev) { return (__assign(__assign({}, prev), { collection: undefined })); });
                        return [2 /*return*/];
                    }
                    return [4 /*yield*/, resolveCollectionById(collectionId)];
                case 1:
                    collection = _a.sent();
                    if (requestId !== collectionRequestIdRef.current)
                        return [2 /*return*/];
                    setDraft(function (prev) { return (__assign(__assign({}, prev), { collection: collection !== null && collection !== void 0 ? collection : { id: collectionId } })); });
                    return [2 /*return*/];
            }
        });
    }); }, [resolveCollectionById, setDraft]);
    var contentData = useContentRows(collectionFields);
    var modalFields = useMemo(function () {
        return [
            {
                key: "collection",
                labelId: "ui-br-agency.title.collection.label",
                type: "selection",
                required: true,
                colSpan: 4,
                selectOptions: collectionOptions,
                onFilter: onCollectionFilter,
                onSelect: handleCollectionSelect,
                action: canCreateCollection
                    ? {
                        labelId: "ui-br-agency.title.collections.form.createNewCollection",
                        onClick: function () { return setCreateCollectionOpen(true); },
                        colSpan: 3,
                    }
                    : undefined,
            },
            {
                key: "collection",
                id: "collectionType",
                labelId: "ui-br-agency.title.collections.type.label",
                type: "text",
                colSpan: 3,
                disabled: true,
                valueFormatter: function (d) {
                    var _a;
                    return ((_a = d.collection) === null || _a === void 0 ? void 0 : _a.type)
                        ? translateCollectionType(d.collection.type, formatMessage)
                        : "";
                },
            },
            {
                key: "partNumber",
                labelId: "ui-br-agency.title.collection.partNumber.label",
                type: "text",
                colSpan: 2,
            },
        ];
    }, [
        collectionOptions,
        onCollectionFilter,
        handleCollectionSelect,
        canCreateCollection,
        formatMessage,
    ]);
    var columns = [
        {
            name: formatMessage({ id: "ui-br-agency.title.collections.type.label" }),
            cellRenderer: function (row) {
                var _a;
                return ((_a = row.collection) === null || _a === void 0 ? void 0 : _a.type)
                    ? translateCollectionType(row.collection.type, formatMessage)
                    : "-";
            },
        },
        {
            name: formatMessage({ id: "ui-br-agency.title.collections.name.label" }),
            cellRenderer: function (row) {
                var _a, _b;
                var name = ((_a = row.collection) === null || _a === void 0 ? void 0 : _a.mainName)
                    ? formatCollectionOptionLabel(row.collection, formatMessage)
                    : "-";
                var collectionId = (_b = row.collection) === null || _b === void 0 ? void 0 : _b.id;
                return collectionId && name !== "-" ? (React.createElement(TextLink, { href: "/bragency/collections/detail/".concat(collectionId), target: "_blank", rel: "noopener noreferrer" }, name)) : (name);
            },
        },
        {
            name: formatMessage({
                id: "ui-br-agency.title.collections.partNumber.label",
            }),
            cellRenderer: function (row) { return row.partNumber || "-"; },
        },
    ];
    return (React.createElement(React.Fragment, null,
        React.createElement(TitleIssnMediumWarning, { titleId: titleId, titleCollections: watchedCollections }),
        React.createElement(Row, null,
            React.createElement(Col, { xs: 12 }, collectionFields.length > 0 ? (React.createElement(EntityRowList, { data: contentData, columns: columns, onEdit: function (row) {
                    return openEditor(getValues("titleCollections.".concat(row._index)), row._index);
                }, onDelete: function (row) { return removeCollection(row._index); } })) : (React.createElement("div", { className: css.emptyState },
                React.createElement(FormattedMessage, { id: "ui-br-agency.title.collections.empty" }))))),
        errorMessages.length > 0 && (React.createElement(Row, { "data-error-field": "titleCollections" },
            React.createElement(Col, { xs: 12 },
                React.createElement(MessageBanner, { type: "error", className: css.tableErrorBanner }, errorMessages.map(function (msg) { return (React.createElement("div", { key: msg }, msg)); }))))),
        React.createElement(FormSectionModal, { open: isOpen, isEditing: isEditing, draft: draft, onDraftChange: setDraft, onSave: saveItem, onClose: closeEditor, addLabelId: "ui-br-agency.title.collections.form.add.title", editLabelId: "ui-br-agency.title.collections.form.edit.title", size: "large", fields: modalFields, validate: function (d) { var _a; return !!((_a = d.collection) === null || _a === void 0 ? void 0 : _a.id); } }),
        React.createElement(CreateCollectionInlineModal, { open: createCollectionOpen, onClose: function () { return setCreateCollectionOpen(false); }, onCreated: handleCreatedCollection }),
        React.createElement(Row, { className: css.actions },
            React.createElement(Col, { xs: 12 },
                React.createElement(Button, { buttonStyle: "primary", onClick: function () { return openEditor(); } },
                    React.createElement(FormattedMessage, { id: "ui-br-agency.title.form.add.collection" }))))));
};
export { TitleCollectionsForm };
